import { Component, Input, Output, OnChanges, OnInit, EventEmitter, ViewEncapsulation } from '@angular/core';
import { QueueViewColumn } from '../models/column.interface';
import { cloneDeep } from 'lodash';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { QueueViewColumnSettings } from '../models/column-settings.interface';
import { CustomValidators } from 'shared/validators/custom-validators';

@Component({
  selector: 'app-column-picker',
  templateUrl: './column-picker.component.html',
  styleUrls: ['./column-picker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ColumnPickerComponent implements OnInit, OnChanges {
  public columnSortBy: string;
  public filterText: string = '';
  public isEditMode: boolean;
  public conditionForm: UntypedFormGroup = new UntypedFormGroup({});

  private _cachedColumnSettings: QueueViewColumnSettings[];

  @Input()
  columnSettings: QueueViewColumnSettings[];

  @Input()
  columnsWithTimeCondition;

  @Output()
  saveChanges: EventEmitter<QueueViewColumnSettings[]> = new EventEmitter();

  constructor(private formBuilder: UntypedFormBuilder) {
  }

  ngOnInit() {
    this._copyOriginalItemsToCache();
    this.initializeForm();
    this.loadColumns();
  }

  private initializeForm() {
    this._cachedColumnSettings.forEach((col) => {
      if (this.canHaveTimeCondition(col) && this.hasConditionSet(col)) {
        let newFormControl = this.formBuilder.group(this.getFormControlConfig());
        this.conditionForm.addControl(col.id, newFormControl);
      }
    });
  }

  loadColumns() {
    this._cachedColumnSettings.forEach((col) => {
      if (this.conditionForm.contains(col.id)) {
        if (this.areBothCachedConditionsEmptyOrNull(col)) {
          this.setToNoCondition(col);
        } else {
          this.conditionForm.controls[col.id].setValue({ minutes: col.condition.minutes, seconds: col.condition.seconds });
        }
      }
    });
  }

  ngOnChanges() {
    this._copyOriginalItemsToCache();
  }

  private _copyOriginalItemsToCache() {
    this._cachedColumnSettings = [];
    this._cachedColumnSettings = cloneDeep(this.columnSettings);
  }

  isColumnHidden(column: QueueViewColumn): boolean {
    let cachedColumn = this.getSelectedColumn(column);
    return (column && column.id && cachedColumn.isHidden);
  }

  setColumnHidden(column: QueueViewColumnSettings, shouldBeHidden: boolean): void {
    if (!column || !column.id) {
      return;
    }

    this._cachedColumnSettings.forEach((col) => {
      if (col.id === column.id) {
        col.isHidden = shouldBeHidden;
        this.toggleConditionSelectorBasedOnColumnsStatus(col, column);
      }
    });
  }

  isCachedColumnHidden(column: QueueViewColumnSettings): boolean {
    let cachedColumn = this._cachedColumnSettings.find((col) => col.id === column.id);
    return cachedColumn.isHidden;
  }

  toggleConditionSelectorBasedOnColumnsStatus(col: QueueViewColumnSettings, column: QueueViewColumnSettings): void {
    if (col.isHidden) {
      this.setToNoCondition(col);
    } else {
      let newFormControl = this.formBuilder.group(this.getFormControlConfig());
      this.conditionForm.addControl(column.id, newFormControl);
      if (column.condition) {
        this.conditionForm.controls[column.id].setValue({ minutes: column.condition.minutes, seconds: column.condition.seconds });
      }
    }
  }

  cancelClicked(): void {
    this.saveChanges.emit(null);
  }

  saveClicked(): void {
    this._cachedColumnSettings.forEach((col) => {
      if (this.conditionForm.controls[col.id]) {
        if (this.areBothConditionsEmptyOrNull(col)) {
          col.condition = null;
        } else if (this.isSecondCondIsNullOrEmpty(col)) {
          col.condition = { minutes: this.conditionForm.controls[col.id].value.minutes, seconds: '0'};
        } else if (this.isMinuteCondIsNullOrEmpty(col)) {
          col.condition = { minutes: '0', seconds: this.conditionForm.controls[col.id].value.seconds};
        } else {
          col.condition = this.conditionForm.controls[col.id].value;
        }
      }
    });
    this.saveChanges.emit(this._cachedColumnSettings);
  }

  clickToEdit(column: QueueViewColumn) {
    let newFormControl = this.formBuilder.group(this.getFormControlConfig());
    this.conditionForm.addControl(column.id, newFormControl);
  }

  get isFormValid(): boolean {
    return this.conditionForm && this.conditionForm.valid;
  }

  private canHaveTimeCondition(column: QueueViewColumnSettings): boolean {
    let cachedColumn = this.columnsWithTimeCondition.find((col) => col.id === column.id);
    return !!cachedColumn;
  }

  private hasConditionSet(column: QueueViewColumnSettings) {
    let cachedColumn = this.getSelectedColumn(column);
    return cachedColumn ? cachedColumn.condition : false;
  }

  private getSelectedColumn(column): QueueViewColumnSettings {
    return this._cachedColumnSettings.find((col) => col.id === column.id);
  }

  private setToNoCondition(column: QueueViewColumnSettings) {
    let cachedColumn = this.getSelectedColumn(column);
    cachedColumn.condition = null;
    this.conditionForm.removeControl(column.id);
  }

  private getFormControlConfig() {
    return {
      minutes: new UntypedFormControl('', Validators.compose([
        Validators.pattern(/^[0-9 ]+$/),
        Validators.maxLength(3),
        Validators.max(999),
        CustomValidators.notContainsWhitespaceValidator])),
      seconds: new UntypedFormControl('', Validators.compose([
        Validators.maxLength(2),
        Validators.pattern(/^[0-9 ]+$/),
        Validators.max(59),
        CustomValidators.notContainsWhitespaceValidator
      ]))
    };
  }

  private areBothCachedConditionsEmptyOrNull(col: QueueViewColumnSettings) {
    return (col.condition.minutes === '0' && col.condition.seconds === '0') || (col.condition.minutes === '' && col.condition.seconds === '');
  }

  private areBothConditionsEmptyOrNull(col: QueueViewColumnSettings) {
    return (this.conditionForm.controls[col.id].value.minutes === '0' && this.conditionForm.controls[col.id].value.seconds === '0') || (this.conditionForm.controls[col.id].value.minutes === '' && this.conditionForm.controls[col.id].value.seconds === '');
  }

  private isMinuteCondIsNullOrEmpty(col: QueueViewColumnSettings) {
    return (this.conditionForm.controls[col.id].value.minutes === '0' || this.conditionForm.controls[col.id].value.minutes === '');
  }

  private isSecondCondIsNullOrEmpty(col: QueueViewColumnSettings) {
    return (this.conditionForm.controls[col.id].value.seconds === '0' || this.conditionForm.controls[col.id].value.seconds === '');
  }
}
